import React from 'react';
import { Box, Stack, Typography, Breadcrumbs, Link, type SxProps, type Theme } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

interface Crumb {
  label: string;
  href?: string;
  onClick?: () => void;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  badges?: React.ReactNode;
  breadcrumbs?: Crumb[];
  actions?: React.ReactNode;
  mb?: number;
  /** Full-bleed divider below the header row. Matches AppShell main padding offsets. */
  divider?: boolean;
  sx?: SxProps<Theme>;
}

export function PageHeader({
  title,
  subtitle,
  icon,
  badges,
  breadcrumbs,
  actions,
  mb = 2.5,
  divider = true,
  sx,
}: PageHeaderProps) {
  return (
    <Box
      sx={{
        mb,
        mx: divider ? { xs: -1.5, md: -2 } : undefined,
        px: divider ? { xs: 1.5, md: 2 } : undefined,
        pb: divider ? 2 : undefined,
        borderBottom: divider ? '1px solid' : 'none',
        borderColor: 'divider',
        ...sx,
      }}
    >
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1 }}>
          {breadcrumbs.map((crumb, idx) =>
            idx === breadcrumbs.length - 1 ? (
              <Typography key={idx} variant="caption" color="text.primary" fontWeight={600}>
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={idx}
                component="button"
                variant="caption"
                color="text.secondary"
                underline="hover"
                onClick={crumb.onClick}
                sx={{ cursor: 'pointer' }}
              >
                {crumb.label}
              </Link>
            ),
          )}
        </Breadcrumbs>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            spacing={1.1}
            sx={{ alignItems: 'center', flexWrap: 'wrap', mb: subtitle ? 0.5 : 0, rowGap: 0.75 }}
          >
            {icon ? (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                  '& .MuiSvgIcon-root': { fontSize: 24 },
                }}
              >
                {icon}
              </Box>
            ) : null}
            <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
              {title}
            </Typography>
            {badges}
          </Stack>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {actions ? (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: { sm: 'center' },
              flex: 1,
              minWidth: 0,
              justifyContent: 'flex-end',
            }}
          >
            {actions}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
